import { useState, useEffect } from 'react';
import PageHeading from '../../components/PageHeading';
import {
  SearchOutlined,
  EditOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  BookOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { debounce } from 'lodash';
import {
  Button,
  Input,
  message,
  Table,
  Select,
  DatePicker,
  Tag,
  Popconfirm
} from 'antd';
import axiosInstance from '../../utils/axios';
import { useNavigate } from 'react-router-dom';
import fetchMiniList from '../../utils/miniFetcher.js';

const { RangePicker } = DatePicker;

const Shows = () => {
  const navigate = useNavigate();
  const [shows, setShows] = useState({
    results: []
  });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [movies, setMovies] = useState([]);
  const [theaters, setTheaters] = useState([]);
  const [times, setTimes] = useState([]);

  const [filters, setFilters] = useState({
    movie: null,
    theatre: null,
    timeslot: null,
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    fetchMasters();
  }, []);

  useEffect(() => {
    const delayFetch = debounce(() => {
      fetchData();
    }, 550); // 550ms debounce

    delayFetch();

    return () => delayFetch.cancel();

    // eslint-disable-next-line
  }, [search, page, filters]);

  const fetchMasters = async () => {
    try {
      const movieResponse = await fetchMiniList("movie/select");
      movieResponse.status ? setMovies(movieResponse.data) : message.error(movieResponse.data);

      const timeResponse = await fetchMiniList("core/timeslot/");
      timeResponse.status ? setTimes(timeResponse.data) : message.error(timeResponse.data);

      const theaterResponse = await fetchMiniList("theatre-management/select");
      theaterResponse.status ? setTheaters(theaterResponse.data) : message.error(theaterResponse.data);

    } catch (error) {
      console.error("Error in fetchMasters:", error);
      message.error("An unexpected error occurred while fetching data");
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  }

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("search", search);

      if (filters.movie) params.append("movie__name__icontains", filters.movie);
      if (filters.theatre) params.append("theatre__name__icontains", filters.theatre);
      if (filters.timeslot) params.append("showdate__showtimeslot__timeslot__start_time__icontains", filters.timeslot);
      if (filters.startDate) params.append("showdate__date__gte", filters.startDate);
      if (filters.endDate) params.append("showdate__date__lte", filters.endDate);

      const response = await axiosInstance.get(`show-management/shows/?${params.toString()}`);
      setShows(response.data);
    } catch (error) {
      message.error("Failed to fetch shows");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Movie Name',
      dataIndex: 'movie_name',
      key: 'movie_name',
    },
    {
      title: 'Theatre Name',
      dataIndex: 'theatre_name',
      key: 'theatre_name',
    },
    {
      title: 'Screen',
      dataIndex: 'screen_name',
      key: 'screen_name',
    },
    {
      title: 'Dates',
      dataIndex: 'dates',
      key: 'dates',
      render: (_, record) => {
        if (!record.dates || record.dates.length === 0) return null;
        const firstDate = record.dates[0].date;
        const lastDate = record.dates[record.dates.length - 1].date;

        return (
          <>
            <Tag color="green">{firstDate}</Tag>
            {firstDate !== lastDate && <>To <Tag color="blue">{lastDate}</Tag></>}
          </>
        );
      }
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button color="cyan" variant="link" icon={<BookOutlined />} onClick={() => navigate(`/admin/shows/reserve/${record.id}/${record.movie}`)} />
          <Button type="link" icon={<EditOutlined />} onClick={() => navigate(`/admin/shows/edit/${record.id}/${record.movie}`)} />
          <Button type="link" icon={<FileTextOutlined />} onClick={() => navigate(`/admin/shows/booking-summary/${record.id}`, { state: { showData: record } })} />
          <Popconfirm
            title="Delete"
            description="Deleting show will crash booking if booking is undergoing, Are you sure to delete this item?"
            onConfirm={() => confirm(record.id)}
            onCancel={cancel}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </>
      )
    },
  ];

  const cancel = () => message.info('Cancelled');

  const confirm = async (id) => {
    try {
      await axiosInstance.delete(`show-management/shows/${id}/`);
      message.success("Deleted successfully");
      fetchData();
    } catch (err) {
      message.error("Cannot delete");
    }

  };

  return (
    <div className="dashboard">
      <PageHeading name="Current Shows" icon={<ClockCircleOutlined />} />

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16
      }}>
        <Input
          placeholder="Search here"
          prefix={<SearchOutlined />}
          style={{ width: "190px" }}
          onChange={(e) => handleSearch(e)}
        />
        <div className="filters">
          <Select
            showSearch
            placeholder="Movies"
            style={{ width: 180, margin: "3px" }}
            value={filters.movie}
            onChange={(value) => setFilters(prev => ({ ...prev, movie: value }))}
            options={movies.map(m => ({ label: `${m.name} (${m.language})`, value: m.name }))}
            allowClear
          />&nbsp;
          <Select
            showSearch
            placeholder="Theatre"
            style={{ width: 160, margin: "3px" }}
            value={filters.theatre}
            onChange={(value) => setFilters(prev => ({ ...prev, theatre: value }))}
            options={theaters.map(t => ({ label: t.name, value: t.name }))}
            allowClear
          />&nbsp;
          <Select
            placeholder="TimeSlots"
            style={{ width: 150, margin: "3px" }}
            value={filters.timeslot}
            onChange={(value) => setFilters(prev => ({ ...prev, timeslot: value }))}
            options={times.map(t => ({
              label: `${t.start_time} - ${t.end_time}`,  // ← Shows both times
              value: t.start_time,

            }))}
            allowClear
          />&nbsp;
          <RangePicker
            style={{ margin: "3px" }}
            onChange={(dates) => {
              if (dates) {
                setFilters(prev => ({ ...prev, startDate: dates[0].format("YYYY-MM-DD"), endDate: dates[1].format("YYYY-MM-DD") }));
              } else {
                setFilters(prev => ({ ...prev, startDate: null, endDate: null }));
              }
            }}
          />&nbsp;
          <Button onClick={() => setFilters({ movie: null, theatre: null, timeslot: null, startDate: null, endDate: null })}>
            Clear
          </Button>
        </div>
      </div>

      <Table
        dataSource={shows.results}
        loading={loading}
        columns={columns}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: 20,
          total: shows.count,
          onChange: (newPage, _) => {
            setPage(newPage);
          }
        }}
      />

    </div>
  );
}

export default Shows;
